import {NOW, REVIEW_STATE, EVENT_KIND, FREEZE_LEVEL} from '../data/reference'
import {int} from '../format'
import {round2, chargeableKg, withinTolerance} from '../weightMath'

/**
 * Weight Freeze (v2) — Initiate: turning a selection into a formal request.
 *
 * Everything here is pure. The drawer holds the form state; this module owns
 * what the numbers mean, what is still missing, and what gets written.
 */

export const DECLARATION = 'I confirm the declared packing material will be used for these shipments.'

export const LEVELS = [
  {
    v: FREEZE_LEVEL.SUBCAT,
    l: 'Sub-category',
    why: 'The business default. A freeze is agreed for a group of similar products, not one code at a time.',
  },
  {
    v: FREEZE_LEVEL.SKU,
    l: 'SKU',
    why: 'Technically possible and sometimes right, for a SKU that behaves nothing like its neighbours.',
  },
]

/** Weighted by shipment count, so a 4,000-shipment SKU is not averaged flat against a 12-shipment one. */
export function weightedAvg(rows, key) {
  let w = 0
  let s = 0
  rows.forEach((r) => {
    const q = r.ships || 1
    w += q
    s += (Number(r[key]) || 0) * q
  })
  return w ? round2(s / w) : 0
}

/** The value carrying the most shipments, not the value on the most rows. */
export function commonest(rows, key) {
  const tally = {}
  rows.forEach((r) => {
    const v = r[key]
    if (v) tally[v] = (tally[v] || 0) + (r.ships || 1)
  })
  const keys = Object.keys(tally)
  if (!keys.length) return ''
  return keys.reduce((a, b) => (tally[b] > tally[a] ? b : a))
}

/**
 * Pre-fill for the proposed weights: dead weight anchored to the product,
 * volumetric anchored to the packing material, chargeable to the most
 * probable weight actually billed.
 */
export function deriveProposal(covered, packs) {
  return {
    deadKg: weightedAvg(covered, 'deadKg'),
    volKg: weightedAvg(covered, 'volKg'),
    chargeKg: weightedAvg(covered, 'mpw'),
    packId: commonest(covered, 'packId') || (packs[0] ? packs[0].id : ''),
  }
}

/**
 * Chargeable weight is normally the higher of dead and volumetric (the same
 * `chargeableKg` every other section uses — never a second `Math.max`). The
 * comparison against the merchant's proposed figure uses the module-wide
 * `withinTolerance` (0.005 kg): a merchant who types 1.004 against a
 * computed 1.00 has not proposed a different weight.
 */
export function weightFacts(dead, vol, charge) {
  const d = Number(dead) || 0
  const v = Number(vol) || 0
  const c = Number(charge) || 0
  const higher = chargeableKg(d, v)
  const governs = d === v ? 'both' : d > v ? 'dead' : 'vol'
  return {higher, governs, mismatch: higher > 0 && !withinTolerance(c, higher)}
}

/**
 * ---- the DWS gate ----
 * A freeze is a claim about what a SKU really weighs, and Prozo can only put
 * that claim to a courier against DWS data for it. The gate is on the
 * MERCHANT's own evidence, not on both sources together: sorter images arrive
 * from the courier whether the merchant acts or not, so a rule that only
 * fired when both were absent would almost never fire — every row in this
 * data has some sorter coverage — and would blame the merchant for a gap that
 * is not theirs.
 *
 * A row with no packing video or images of its own therefore lands in
 * `blocked`, and a request over it cannot be submitted until the merchant
 * supplies something — which, since the raise drawer stopped linking out to the
 * DWS data page, means attaching it in the drawer itself. `gapsFor` below is
 * where that second route is honoured; this partition only ever reports what is
 * ALREADY ON FILE against a row key, and an in-drawer attachment never moves a
 * row out of `blocked`. A row the courier has never scanned is submittable but
 * weaker, so it lands in `weak` and only warns.
 *
 * Evaluated against `covered`, not the original selection, because the
 * merchant can change what the freeze covers right up to the moment they
 * submit.
 */
export function dwsPartition(covered, evIndex) {
  const rows = covered.map((row) => ({row, ev: evIndex.get(row.key) || {sorter: 0, merchant: 0}}))
  return {
    rows,
    blocked: rows.filter((x) => !x.ev.merchant),
    weak: rows.filter((x) => x.ev.merchant && !x.ev.sorter),
  }
}

/**
 * Typed gap keys, NOT prose. The mockup assembled one English sentence and
 * used it verbatim as footer text, tooltip and aria-label; a copy edit or an
 * i18n pass broke all three at once. Callers get keys; gapSentence below is
 * the only place that knows English.
 */
export const GAP = {
  SUBCAT: 'SUBCAT',
  NO_ROWS: 'NO_ROWS',
  NO_MERCHANT_EVIDENCE: 'NO_MERCHANT_EVIDENCE',
  WEIGHTS: 'WEIGHTS',
  PACK: 'PACK',
  DECLARATION: 'DECLARATION',
  // The new-SKU drawer's five. SUBCAT, PACK and DECLARATION are shared with the
  // raise drawer above — those three gaps ask the identical question in both
  // flows, so neither flow gets a key of its own for them. SUBCAT joined that
  // list when the new-product drawer gained a level chooser: "which
  // sub-category" is the same unanswered question whether the products behind
  // it have shipped or not, and only the source of the OPTIONS differs — the
  // ticked rows' own sub-categories in the raise drawer, the catalogue's
  // {category: [sub, …]} map, flattened, in the new-product one.
  PRODUCT_NAME: 'PRODUCT_NAME',
  SKU_CODE: 'SKU_CODE',
  DIMENSIONS: 'DIMENSIONS',
  DEAD_WEIGHT: 'DEAD_WEIGHT',
  EVIDENCE: 'EVIDENCE',
}

export const GAP_ORDER = [
  GAP.SUBCAT, GAP.NO_ROWS, GAP.NO_MERCHANT_EVIDENCE, GAP.WEIGHTS, GAP.PACK, GAP.DECLARATION,
]

/**
 * The new-SKU drawer's own order. `GAP_ORDER` above stays exactly as it is — it
 * is the raise drawer's contract and a test pins it — because the two flows are
 * missing different things and would read them in a different order anyway.
 *
 * Order follows the drawer top to bottom — name the product and the code it
 * will ship under, then say what the freeze reaches, then the material, then
 * the product's weight and the box it goes in, then the promise, then the
 * proof. NOTHING RENDERED READS THAT ORDER any more: `gapSentence` below
 * returns one fixed line whatever is open, and the drawer reads only
 * `gaps.length`. It is kept ordered anyway because the list is also the
 * inventory of this drawer's gates, and a reader adding a gate needs one
 * obvious place to put it — matching the screen is that place. Reordering this
 * array changes no user-visible text; only the unit test observes it.
 */
export const NEW_SKU_GAP_ORDER = [
  GAP.PRODUCT_NAME, GAP.SKU_CODE, GAP.SUBCAT, GAP.PACK, GAP.DEAD_WEIGHT, GAP.DIMENSIONS,
  GAP.DECLARATION, GAP.EVIDENCE,
]

/**
 * Does the drawer's own capture hold anything at all?
 *
 * Takes the immutable `{video, images[]}` shape `buildEvidenceFiles` returns —
 * never the drawer's live `images` state object — so nothing mutable crosses
 * this boundary. One video is enough, and so is one still: a freeze request is
 * a proposal about what a SKU weighs, and either kind of reading is something
 * Prozo can put in front of a courier.
 */
export function hasAttachedEvidence(files) {
  if (!files) return false
  return !!files.video || (files.images || []).length > 0
}

/**
 * ---- what `attachedEvidence` means, exactly ----
 *
 * `blocked` is PER ROW: it comes from `dwsPartition`, which asks the evidence
 * index what is already on file against each row key. `attachedEvidence` is ONE
 * BOOLEAN FOR THE WHOLE REQUEST: the video and stills captured in the raise
 * drawer are attached to the request, and `buildEvidenceFiles` records no row
 * key against them — the capture form never asks which row was filmed.
 *
 * The semantics chosen here: one attachment clears GAP.NO_MERCHANT_EVIDENCE for
 * EVERY row the request covers, at any row count — not only for a one-row
 * request. Three reasons:
 *
 *   1. A request proposes ONE dead/volumetric/chargeable triple for its whole
 *      covered set. A single set of readings is the evidence for that single
 *      claim; there is no per-row claim for per-row evidence to answer.
 *   2. Attributing the capture to one particular row would invent a link the
 *      data model does not hold. Row-keyed evidence exists — it is what the
 *      evidence index counts — and this is deliberately not that.
 *   3. Sub-category level is the module's stated business default ("a freeze is
 *      agreed for a group of similar products, not one code at a time"), and
 *      ~45% of rows carry no merchant evidence. Clearing only one-row requests
 *      would answer the common case with "raise it N times, film it N times".
 *
 * The honesty this owes is in the WORDING, not in the gate: `evidenceScope`
 * below is the one place that says what an attachment covers, and for a
 * multi-row request it says plainly that it is one sample behind one proposed
 * weight rather than each row evidenced in its own right.
 *
 * Nothing else moves. `declared` stays a required gap — the packing-material
 * declaration is a promise about future shipments, and no amount of film
 * substitutes for it — and SUBCAT / NO_ROWS / WEIGHTS / PACK are all missing
 * facts about the proposal itself that evidence cannot supply.
 */
export function gapsFor({
  level, subcat, covered, blocked, deadKg, volKg, chargeKg, packId, declared, attachedEvidence,
}) {
  const found = []
  if (level === FREEZE_LEVEL.SUBCAT && !subcat) found.push({key: GAP.SUBCAT})
  if (!covered.length) found.push({key: GAP.NO_ROWS})
  if (blocked.length && !attachedEvidence) {
    found.push({
      key: GAP.NO_MERCHANT_EVIDENCE,
      count: blocked.length,
      label: blocked.length === 1 ? blocked[0].row.label : '',
    })
  }
  if (!(Number(deadKg) > 0) || !(Number(volKg) > 0) || !(Number(chargeKg) > 0)) found.push({key: GAP.WEIGHTS})
  if (!packId) found.push({key: GAP.PACK})
  if (!declared) found.push({key: GAP.DECLARATION})
  return GAP_ORDER.map((k) => found.find((g) => g.key === k)).filter(Boolean)
}

/**
 * The new-SKU gate. A product with no shipment history is missing different
 * things from a selection of rows, so this is its own function rather than a
 * branch inside `gapsFor` — but it returns the same `{key, …}` shape, ordered by
 * its own list, and `gapSentence` below turns both into English.
 *
 * `volKg` is the ALREADY-DERIVED volumetric, `null` when the merchant has not
 * finished the triple. It is passed in rather than derived here so that
 * `packing/state/packingModel.js`'s `volumetric` stays the single definition of
 * a usable triple in the module; `== null` is deliberate loose equality, because
 * a real 0 (any parcel under 25 cm³) is a measurement and not a gap.
 *
 * There is no WEIGHTS gate. The raise drawer's asks for three weights above
 * zero, but here only dead weight is typed: volumetric follows from the
 * dimensions and chargeable follows from both, so gating on all three would
 * block a legitimately tiny parcel whose volumetric rounds to 0.00.
 *
 * BOTH HALVES OF THE PRODUCT ARE REQUIRED. Every catalogue record carries a
 * `sku` and a `product`, so a declared product with a code and no name is
 * catalogue data born incomplete — and the reviewer, who has no shipment row to
 * read the name off, would be approving a weight for a bare string.
 *
 * The SUBCAT gate is the raise drawer's own, on the same condition: it fires
 * only at Sub-category level, and only while nothing is chosen. Any `level`
 * that is not `FREEZE_LEVEL.SUBCAT` — SKU, or a caller that passed none —
 * leaves it silent, exactly as in `gapsFor`.
 */
export function newSkuGapsFor({
  product, code, clash, level, subcat, volKg, deadKg, packId, declared, attachedEvidence,
}) {
  const found = []
  if (!String(product || '').trim()) found.push({key: GAP.PRODUCT_NAME})
  const trimmed = String(code || '').trim()
  if (trimmed.length < 2) found.push({key: GAP.SKU_CODE, clash: false})
  else if (clash) found.push({key: GAP.SKU_CODE, clash: true})
  if (level === FREEZE_LEVEL.SUBCAT && !subcat) found.push({key: GAP.SUBCAT})
  if (volKg == null) found.push({key: GAP.DIMENSIONS})
  if (!(Number(deadKg) > 0)) found.push({key: GAP.DEAD_WEIGHT})
  if (!packId) found.push({key: GAP.PACK})
  if (!attachedEvidence) found.push({key: GAP.EVIDENCE})
  if (!declared) found.push({key: GAP.DECLARATION})
  return NEW_SKU_GAP_ORDER.map((k) => found.find((g) => g.key === k)).filter(Boolean)
}

/**
 * The footer's blocking line. One sentence whatever is missing, on request.
 *
 * It used to name every outstanding gap, which read well at one or two and
 * became a wall at six — a freshly opened new-product drawer had all of them,
 * and the list ran past 270 characters with semicolons holding it together.
 * The fields themselves carry the red asterisk that says which they are, so
 * the footer's job is only to say that something is still missing.
 *
 * The GAP KEYS stay: `gapsFor` and `newSkuGapsFor` still return a typed list and
 * the submit button gates on its length. The per-gap ENGLISH went with the
 * enumeration — it had exactly one consumer, this function, and keeping a
 * phrase table nothing renders would be dead weight dressed up as a hook.
 *
 * The per-gap DETAILS (`count`, `label`, `clash`) are in the same position, one
 * step short of dead: no renderer reads them. Both drawers' field-level
 * messages compute their own — the code helper reads `validateNewCode`'s
 * `clash` directly, not the gap's. They survive because the tests assert on
 * them and because they are the honest place for that information if a
 * field-level message ever does want it; they are NOT a live contract, and a
 * reader must not add a detail here expecting it to appear on screen.
 */
export function gapSentence(gaps) {
  if (!gaps.length) return ''
  return 'Please fill all the required details here.'
}

/**
 * Typed states for the evidence step, so the drawer maps them to a chip tone
 * and this module never learns the styling vocabulary.
 */
export const EVIDENCE_STATE = {OPTIONAL: 'OPTIONAL', REQUIRED: 'REQUIRED', SATISFIED: 'SATISFIED'}

/**
 * The ONE place that states what an in-drawer attachment does and does not
 * cover. The gate it feeds is request-level (see gapsFor), so the UI carries
 * the whole of the honesty: for a multi-row request the note says outright that
 * this is one sample behind one proposed weight, NOT each row evidenced
 * separately. A merchant who films one parcel and covers five rows should read
 * that from the screen, not infer it.
 *
 * `blockedCount` is how many covered rows have nothing of the merchant's own on
 * file; `coveredCount` is how many rows the request covers in total.
 */
export function evidenceScope({coveredCount, blockedCount, attached}) {
  const sample = 'One set of readings, attached to the request as a whole rather than to any one row.'

  if (!blockedCount) {
    return {
      state: EVIDENCE_STATE.OPTIONAL,
      chip: 'Does not block submission',
      note: attached && coveredCount > 1
        ? sample + ' Every covered row already has DWS data of its own; this is added on top of it.'
        : '',
    }
  }
  if (!attached) {
    return {
      state: EVIDENCE_STATE.REQUIRED,
      chip: blockedCount === 1
        ? 'Required: 1 covered row has no DWS data'
        : 'Required: ' + int(blockedCount) + ' covered rows have no DWS data',
      note: 'One packing video, or one photo, is enough to submit. It is attached to the request as a whole,'
        + ' not to a particular row.',
    }
  }
  return {
    state: EVIDENCE_STATE.SATISFIED,
    chip: 'Attached — enough to submit',
    note: coveredCount === 1
      ? sample + ' This request covers one row, so it is the evidence for that row.'
      : sample + ' It stands as a sample behind the single weight proposed for all ' + int(coveredCount)
        + ' covered rows — it is not ' + int(coveredCount) + ' rows each evidenced in their own right.',
  }
}

export function subjectFor(level, subcat, covered) {
  if (level === FREEZE_LEVEL.SUBCAT) return subcat
  if (covered.length === 1) return covered[0].label
  if (!covered.length) return ''
  return covered[0].label + ' and ' + int(covered.length - 1) + ' more'
}

/**
 * Client-minted so a request appears in Requests & statuses immediately. In a
 * real panel this moves server-side — two merchants raising at once would
 * collide here.
 *
 * The 2-letter prefix ('NB' for Nobero) is NOT derivable from the merchant id
 * string — 'nobero'.slice(0, 2).toUpperCase() is 'NO', while every seeded
 * request in this dataset reads 'WF-NB-####'. So the prefix is read off an
 * EXISTING request id when one is on file (it can then never drift from what
 * is already there), and only falls back to a small explicit map for a
 * merchant with no request history at all yet.
 */
const MERCHANT_PREFIX = {nobero: 'NB'}

export function nextRequestId(requests, merchantId) {
  let mx = 2600
  let prefix = null
  ;(requests || []).forEach((r) => {
    const parts = String(r.id).split('-')
    const n = parseInt(parts[parts.length - 1], 10)
    if (n > mx) mx = n
    if (!prefix && parts.length >= 2) prefix = parts[1]
  })
  if (!prefix) prefix = MERCHANT_PREFIX[merchantId] || String(merchantId).slice(0, 2).toUpperCase()
  return 'WF-' + prefix + '-' + (mx + 1)
}

/**
 * Two different things a request can carry, deliberately counted apart.
 * `evidence` is DWS records already on file that the request itself carries;
 * `evidenceFiles` is the video and stills captured in the drawer. Summing them
 * into one number would tell a reviewer a request holds more scanner records
 * than exist, so the sentence names each source separately.
 *
 * NOTHING IN THE UI CAN PUT A RECORD ON A NEWLY RAISED REQUEST any more. The
 * raise drawer's record picker is gone, and both drawers now send a constant
 * `evidence: 0, evidenceAwbs: []`, so on anything raised here only the `own`
 * half can be non-empty.
 *
 * NOR DOES ANY SEEDED REQUEST REACH THIS FUNCTION. All ten in
 * data/noberoFreeze.js ship a baked `history` whose RAISED event is the literal
 * 'Freeze request raised.', and `buildRequest` — the only caller — runs solely
 * on the newly-raised path (InitiatePage's `handleSubmit`). Their real
 * `evidence`/`evidenceAwbs` values ARE still displayed, but by the details
 * grids in requests/ (RqReviewCard, RqRejectCard, RqApprovedDrawer), never by
 * this sentence. So the two `records` branches below are unreachable in the
 * running app today: they stay because `evidence` remains part of the payload
 * contract documented on `buildRequest`, and a sentence that silently swallowed
 * a count someone put back would be the worse failure — but do not read them as
 * covering anything currently on screen, and do not cite a seed as their cover.
 *
 * `evidenceAwbs` is NOT read here at all. That is why the empty case below
 * names images and uploads rather than claiming the request carries nothing: a
 * payload can hold AWBs this function never sees, and the grids that do show
 * them ('Attached records') would then contradict the timeline beside them.
 */
function raisedWith(payload) {
  const n = payload.evidence || 0
  const files = payload.evidenceFiles || {}
  const shots = (files.images || []).length
  const records = n ? int(n) + ' DWS ' + (n === 1 ? 'image' : 'images') : ''
  const own = [
    files.video ? 'a packing video' : '',
    shots ? int(shots) + (shots === 1 ? ' photograph' : ' photographs') : '',
  ].filter(Boolean).join(' and ')

  // Both halves empty. NOT "without DWS evidence": on a request raised here
  // that claim is usually false. `gapsFor` only demands the merchant's own
  // upload while `blocked` is non-empty, so the merchant who submits with
  // nothing attached is precisely the one every covered row of whose selection
  // ALREADY has DWS data on file. The line therefore says what the request
  // carries and leaves what is on file to the rows, where the reviewer can
  // read it.
  //
  // The trailing clause stays hedged on purpose. This function is handed a
  // payload and nothing else — no partition, no evidence index — so "every
  // covered row already has DWS data" is a caller-side invariant it cannot
  // verify, and asserting it would be the same overclaim in the other
  // direction. What is on file is the rows' story to tell.
  if (!records && !own) {
    return 'Request raised with no DWS images and nothing uploaded with it — any DWS data for the covered'
      + ' rows is whatever is already on file.'
  }
  if (!own) return 'Request raised with ' + records + '.'
  if (!records) return 'Request raised with ' + own + ' uploaded with it.'
  return 'Request raised with ' + records + ', plus ' + own + ' uploaded with it.'
}

/**
 * @param {object} payload from the drawer: level, subject, deadKg, volKg,
 *   chargeKg, packId, covers[], evidence, evidenceAwbs[], evidenceFiles,
 *   remarks, and declaredDims[l, b, h], the packed-parcel dimensions the
 *   merchant typed. Nothing reads declaredDims yet; it rides through the spread
 *   below because it is the sole provenance of `volKg` — both drawers now
 *   derive that figure from the triple rather than taking it typed, and it can
 *   legitimately differ from the declared pack's own.
 * @param {{id: string, name: string}} merchant
 * @param {Array} requests the current list, for id minting
 *
 * `raisedBy` is the MERCHANT's name even on a Prozo-initiated account: this
 * screen is the merchant raising a request, and the drawer's own closing note
 * says "A merchant-raised freeze takes effect only after Prozo approves it."
 * The seeded Nobero requests read 'Prozo Ops' because Prozo raised those.
 */
export function buildRequest(payload, merchant, requests) {
  const id = nextRequestId(requests, merchant.id)
  const today = NOW.toISOString().slice(0, 10)
  const text = raisedWith(payload)
  return {
    ...payload,
    id,
    raisedBy: merchant.name,
    raisedOn: today,
    kind: 'review',
    round: 1,
    state: REVIEW_STATE.AWAITING,
    submissions: 1,
    history: [{date: today, actor: merchant.name, round: 1, kind: EVENT_KIND.RAISED, text}],
  }
}
